import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDatabaseSelect } from '@/store/slices/databaseSlice';
import { setActiveRow, selectActiveRow } from '@/store/slices/ActiveRowState';
import type { Row_spaces, Row_documents } from '@/types/databaseRows';
import type { RootState } from '@/store';
import { Folder } from 'lucide-react';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useMemo } from 'react';

export default function DocumentTreeSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [expandedSpaces, setExpandedSpaces] = useState<Set<string>>(new Set());

  const activeDocumentId = useSelector((state: RootState) =>
    selectActiveRow(state, 'documents')
  );

  // Fetch all spaces
  const { data: spaces, isLoading: spacesLoading } = useDatabaseSelect<Row_spaces>({
    table: 'spaces',
    orderColumn: 'order_index',
    orderOptions: { ascending: true }
  });

  // Fetch all documents
  const { data: documents, isLoading: documentsLoading } = useDatabaseSelect<Row_documents>({
    table: 'documents',
    orderColumn: 'order_index',
    orderOptions: { ascending: true }
  });

  // Group documents by space
  const documentsBySpace = useMemo(() => {
    if (!documents) return {};

    const grouped: Record<string, Row_documents[]> = {};
    documents.forEach(doc => {
      if (doc.space_id) {
        if (!grouped[doc.space_id]) {
          grouped[doc.space_id] = [];
        }
        // Only include root-level documents (no parent_document_id)
        if (!doc.parent_document_id) {
          grouped[doc.space_id].push(doc);
        }
      }
    });

    return grouped;
  }, [documents]);

  // Get child documents for a parent
  const getChildDocuments = (parentId: string): Row_documents[] => {
    if (!documents) return [];
    return documents.filter(doc => doc.parent_document_id === parentId);
  };

  // Toggle space expansion
  const toggleSpace = (spaceId: string) => {
    setExpandedSpaces(prev => {
      const next = new Set(prev);
      if (next.has(spaceId)) {
        next.delete(spaceId);
      } else {
        next.add(spaceId);
      }
      return next;
    });
  };

  const handleDocumentClick = (documentId: string) => {
    dispatch(setActiveRow({ tableName: 'documents', rowId: documentId }));
    navigate(`/document/${documentId}`);
  };

  // Recursive function to render document tree
  const renderDocumentTree = (doc: Row_documents, depth: number = 0): React.ReactNode => {
    const children = getChildDocuments(doc.id);
    const hasChildren = children.length > 0;
    const isActive = activeDocumentId === doc.id;
    const paddingLeft = depth * 12;

    return (
      <div key={doc.id}>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            isActive={isActive}
            className="h-7 text-xs"
            style={{ paddingLeft: `${paddingLeft + 8}px` }}
          >
            <a
              onClick={(e) => {
                e.preventDefault();
                handleDocumentClick(doc.id);
              }}
              className="cursor-pointer flex items-center gap-1"
              data-testid={`sidebar-document-${doc.id}`}
            >
              <span className="truncate flex-1">{doc.title}</span>
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>
        {hasChildren && (
          <div className="ml-2">
            {children.map(child => renderDocumentTree(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (spacesLoading || documentsLoading) {
    return (
      <SidebarGroup>
        <SidebarGroupLabel>Documents</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <Skeleton className="h-8 w-full" />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <Skeleton className="h-8 w-full" />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <Skeleton className="h-8 w-full" />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (!spaces || spaces.length === 0) {
    return null;
  }

  return (
    <SidebarGroup className="flex-1 overflow-auto">
      <SidebarGroupLabel className="text-xs">Documents</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {spaces.map(space => {
            const spaceDocuments = documentsBySpace[space.id] || [];
            const isExpanded = expandedSpaces.has(space.id);
            const hasDocuments = spaceDocuments.length > 0;

            return (
              <Collapsible
                key={space.id}
                open={isExpanded}
                onOpenChange={() => toggleSpace(space.id)}
              >
                <SidebarMenuItem>
                  <div className="flex items-center w-full">
                    <SidebarMenuButton
                      tooltip={space.name}
                      className="flex-1 h-8 text-sm"
                    >
                      <Folder className="h-4 w-4 text-primary" />
                      <span className="flex-1 truncate">{space.name}</span>
                    </SidebarMenuButton>
                    {hasDocuments && (
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0 ml-1"
                          data-testid={`sidebar-space-toggle-${space.id}`}
                        >
                          <ChevronRight
                            className={`h-3 w-3 transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            }`}
                          />
                        </Button>
                      </CollapsibleTrigger>
                    )}
                  </div>
                </SidebarMenuItem>

                {hasDocuments && (
                  <CollapsibleContent>
                    <div className="pl-0" data-testid={`sidebar-documents-container-${space.id}`}>
                      {spaceDocuments.map(doc => renderDocumentTree(doc, 0))}
                    </div>
                  </CollapsibleContent>
                )}
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
